/**
 * Realtime cross-device sync.
 *
 * When another device (or another tab) saves the user's state row, Supabase
 * Realtime broadcasts the change over a websocket. We listen on a per-user
 * channel and apply the new state to the local store  -  the user sees the
 * update appear within ~1s without refreshing.
 *
 * Echo prevention: every cloud save records its updated_at timestamp into
 * `lastSelfWriteAt` (in sync.ts). When a realtime event arrives, we compare
 * its row.updated_at to that timestamp. Equal → it's our own write echoing
 * back, ignore it. Different → another device wrote it, apply.
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './client.js';
import { getLastSelfWriteAt } from './sync.js';

let channel: RealtimeChannel | null = null;
let currentUserId: string | null = null;

const listeners = new Set<(state: unknown) => void>();

/** Subscribe to remote-state-changed events. Returns an unsubscribe function. */
export function onRemoteStateChanged(cb: (state: unknown) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Open a realtime channel for the given user. Idempotent  -  if already
 *  subscribed for this user, no-op; if subscribed for a different user, swap. */
export function subscribeToUserState(userId: string): void {
  if (!supabase) return;
  if (currentUserId === userId && channel) return;
  // Tearing down the old channel before opening a new one (e.g. on user switch)
  if (channel) {
    void supabase.removeChannel(channel);
    channel = null;
  }
  currentUserId = userId;

  channel = supabase
    .channel(`app_state:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',                  // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'app_state',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const row = (payload.new ?? payload.old) as { state?: unknown; updated_at?: string } | null;
        if (!row) return;
        // Echo suppression: ignore the row we just wrote ourselves.
        const selfTs = getLastSelfWriteAt();
        if (selfTs && row.updated_at && row.updated_at === selfTs) return;
        // Notify listeners with the new state blob.
        if (row.state !== undefined) {
          for (const cb of listeners) cb(row.state);
        }
      },
    )
    .subscribe();
}

/** Tear down the active subscription (call on sign-out). */
export function unsubscribeFromUserState(): void {
  if (channel && supabase) {
    void supabase.removeChannel(channel);
  }
  channel = null;
  currentUserId = null;
}
